import React from "react";
import {
  DocumentRow,
} from "../WerkbonViewShared";
import ExtraTimeRequestButton from "../../time-requests/ExtraTimeRequestButton";
import SignaturePadField from "./SignaturePadField";
import {
  formatDatumTijd,
  formatPhotoCategory,
  formatShortDate,
  formatTimeRequestStatus,
  getTimeRequestStatusClassName,
  shouldPreviewDocument,
  toDocumentUrl,
} from "../../../pages/monteur/workbonViewUtils";
import {
  getDemoClientName,
  getDemoLocationName,
  getDemoWorkorderNumber,
  getDemoWorkorderTitle,
  useMarketingDemoMode,
} from "../../../utils/marketingDemo";

function toTextList(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }
  const text = String(value || "").trim();
  return text ? [text] : [];
}

function getContactSummary(werkbon) {
  const name =
    werkbon?.contact?.name ||
    werkbon?.contactNaam ||
    werkbon?.klant?.contactpersoon ||
    werkbon?.client?.hoofdcontactpersoon ||
    "";
  const phone = werkbon?.contact?.phone || werkbon?.contactTelefoon || "";
  const email = werkbon?.contact?.email || werkbon?.contactEmail || "";
  return [name, phone, email].filter(Boolean).join(" • ") || "-";
}

function getClientSummary(werkbon) {
  return (
    werkbon?.clientName ||
    werkbon?.client?.naam ||
    werkbon?.clientId?.naam ||
    werkbon?.klant?.naam ||
    "-"
  );
}

function getLocationSummary(werkbon, marketingDemoMode) {
  if (marketingDemoMode) return getDemoLocationName();
  return werkbon?.locationText || werkbon?.opdracht?.locatie || werkbon?.locatie || "-";
}

function getScopeItems(werkbon) {
  return [
    ...toTextList(werkbon?.scope?.binnen),
    ...toTextList(werkbon?.scope?.buiten),
    ...toTextList(werkbon?.scope?.toelichting),
  ];
}

function getCompactMaterialRows(materialEntries = []) {
  return Array.isArray(materialEntries)
    ? materialEntries.filter((entry) => String(entry?.description || "").trim())
    : [];
}

function formatExecutionModeLabel(isAutoTimeMode) {
  return isAutoTimeMode ? "Automatische tijd" : "Handmatige tijd";
}

function ExecutionMetaItem({ label, value, emphasized = false }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 text-sm ${emphasized ? "font-semibold text-slate-900" : "text-slate-700"}`}>
        {value || "-"}
      </p>
    </div>
  );
}

function ExecutionMainActionsPanel({
  werkbon,
  canStartWerkbon,
  canResumeWerkbon,
  canBlockWerkbon,
  canAfrondenWerkbon,
  canSelfAssignWerkbon,
  actieLoading,
  handleUnblockWerkbon,
  handleStartWerkbon,
  handleSelfAssignWerkbon,
  openStopReasonPicker,
  handleAfrondenWerkbon,
  showStopReasonPicker,
  STOP_REASON_OPTIONS,
  handleBlockWerkbon,
  lastStopReason,
  setShowStopReasonPicker,
  werkbonError,
  showPdfAction = false,
  downloadWorkorderPdf,
  workorderPdfLoading = false,
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-gray-900">Hoofdacties</p>
        <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-700">
          Uitvoering
        </span>
      </div>
      {String(werkbon?.status || "").toLowerCase() === "in_uitvoering" && (
        <p className="mb-2 text-xs text-gray-600">
          Werk bezig sinds: {formatDatumTijd(werkbon?.startedAt)}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {canSelfAssignWerkbon && (
          <button
            onClick={handleSelfAssignWerkbon}
            disabled={actieLoading}
            className="min-h-[42px] rounded border border-blue-300 bg-white px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-60"
          >
            Zelf inplannen
          </button>
        )}
        {(canStartWerkbon || canResumeWerkbon) && (
          <button
            onClick={canResumeWerkbon ? handleUnblockWerkbon : handleStartWerkbon}
            disabled={actieLoading}
            className="min-h-[42px] rounded bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60"
          >
            Start werk
          </button>
        )}
        {canBlockWerkbon && (
          <button
            onClick={openStopReasonPicker}
            disabled={actieLoading}
            className="min-h-[42px] rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
          >
            Stop werk
          </button>
        )}
        {canAfrondenWerkbon && (
          <button
            onClick={handleAfrondenWerkbon}
            disabled={actieLoading}
            className="min-h-[42px] rounded border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 disabled:opacity-60"
          >
            Werk gereed
          </button>
        )}
        {showPdfAction && typeof downloadWorkorderPdf === "function" && (
          <button
            type="button"
            onClick={downloadWorkorderPdf}
            disabled={workorderPdfLoading}
            className="min-h-[42px] rounded border border-blue-300 bg-white px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-60"
          >
            {workorderPdfLoading ? "PDF laden..." : "Werkbon PDF"}
          </button>
        )}
      </div>
      {showStopReasonPicker && canBlockWerkbon && (
        <div className="mt-3 rounded border border-red-200 bg-red-50 p-3">
          <p className="mb-2 text-sm font-semibold text-red-800">Waarom stop je?</p>
          <div className="grid gap-2 md:grid-cols-2">
            {STOP_REASON_OPTIONS.map((reason) => (
              <button
                key={reason.code}
                type="button"
                onClick={() => handleBlockWerkbon(reason)}
                disabled={actieLoading}
                className="rounded border border-red-200 bg-white px-3 py-2 text-left text-sm text-red-900 hover:bg-red-100 disabled:opacity-60"
              >
                {reason.text}
              </button>
            ))}
          </div>
          {lastStopReason && (
            <button
              type="button"
              onClick={() => handleBlockWerkbon(lastStopReason)}
              disabled={actieLoading}
              className="mt-3 rounded border border-red-300 bg-white px-3 py-2 text-sm text-red-900 hover:bg-red-100 disabled:opacity-60"
            >
              Laatste reden opnieuw gebruiken: {lastStopReason.text}
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowStopReasonPicker(false)}
            className="mt-3 rounded px-2 py-1 text-xs text-gray-600 hover:text-gray-800"
          >
            Annuleren
          </button>
        </div>
      )}
      {!showStopReasonPicker && canBlockWerkbon && lastStopReason && (
        <button
          type="button"
          onClick={() => handleBlockWerkbon(lastStopReason)}
          disabled={actieLoading}
          className="mt-3 rounded border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          Laatste reden: {lastStopReason.text} (opnieuw gebruiken)
        </button>
      )}
      {String(werkbon?.status || "").toLowerCase() === "gepland" && (
        <p className="mt-2 text-xs text-gray-500">Klaar om te starten.</p>
      )}
      {String(werkbon?.status || "").toLowerCase() === "tijdelijk_stilgelegd" && (
        <p className="mt-2 text-xs text-gray-500">
          Werk staat stil. Gebruik "Start werk" om door te gaan.
        </p>
      )}
      {String(werkbon?.status || "").toLowerCase() === "afgerond" && (
        <p className="mt-2 text-xs text-gray-500">Werkbon is gereed gemeld.</p>
      )}
      {String(werkbon?.status || "").toLowerCase() === "in_uitvoering" && !canAfrondenWerkbon && (
        <p className="mt-2 text-xs text-gray-500">Werkbon is in uitvoering.</p>
      )}
      {!canSelfAssignWerkbon && !canStartWerkbon && !canResumeWerkbon && !canBlockWerkbon && !canAfrondenWerkbon && (
        <p className="mt-2 text-xs text-gray-500">Geen acties beschikbaar voor deze status.</p>
      )}
      {actieLoading && <p className="mt-2 text-xs text-gray-500">Bezig met verwerken...</p>}
      {werkbonError && <p className="mt-2 text-xs text-red-600">{werkbonError}</p>}
    </div>
  );
}

function ExecutionOverviewPanel({
  werkbon,
  compactProjectLayout,
  sanitizedWorkorderNumber,
  sanitizedSummaryTitle,
  clientSummary,
  sanitizedLocation,
  contactSummary,
  isAutoTimeMode,
  actionsProps,
}) {
  const statusLabel = String(werkbon?.status || "-");
  const startLocationValue = [werkbon?.plannedStartTime || "-", sanitizedLocation]
    .filter(Boolean)
    .join(" • ");

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="border-b border-slate-200 px-4 py-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Werkbon {sanitizedWorkorderNumber}
            </p>
            <h4 className="mt-1 text-lg font-semibold text-slate-900">
              {sanitizedSummaryTitle}
            </h4>
            <p className="mt-1 text-sm text-slate-600">
              {compactProjectLayout ? clientSummary : sanitizedLocation}
            </p>
          </div>
          <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
            {statusLabel}
          </span>
        </div>
      </div>

      <div className="grid gap-4 p-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.85fr)]">
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <ExecutionMetaItem label="Werkbon" value={sanitizedWorkorderNumber} emphasized />
            <ExecutionMetaItem
              label={compactProjectLayout ? "Klant" : "Locatie"}
              value={compactProjectLayout ? clientSummary : sanitizedLocation}
            />
            <ExecutionMetaItem
              label={compactProjectLayout ? "Start / locatie" : "Status"}
              value={compactProjectLayout ? startLocationValue : statusLabel}
            />
            <ExecutionMetaItem
              label={compactProjectLayout ? "Contactpersoon" : "Tijdregistratie"}
              value={compactProjectLayout ? contactSummary : formatExecutionModeLabel(isAutoTimeMode)}
            />
          </div>
        </div>

        <div className="space-y-3">
          <ExecutionMainActionsPanel {...actionsProps} />
        </div>
      </div>
    </div>
  );
}

function getCompletionMissingItems(
  completionFlow,
  completionState,
  signatureReadyOverride = false,
  signatureRequiredOverride = null
) {
  const missingKeys = Array.isArray(completionState?.missing) ? completionState.missing : [];
  const items = [];

  for (const key of missingKeys) {
    if (key === "executionLog") items.push("Daglog/uitvoeringstekst");
    if (key === "hours") items.push("Tijdregistratie");
    if (key === "materials") items.push("Materiaalregistratie");
    if (key === "evidence") items.push("Foto of bijlage");
  }

  const signatureRequired =
    signatureRequiredOverride === null
      ? completionFlow?.signatureRequired === true
      : signatureRequiredOverride === true;
  const signatureReady =
    completionFlow?.signatureCaptured === true || signatureReadyOverride === true;
  if (signatureRequired && !signatureReady) {
    items.push("Klant-handtekening");
  }

  return Array.from(new Set(items));
}

function getStatusPillClassName(statusKey) {
  const key = String(statusKey || "").trim().toLowerCase();
  if (["ready", "generated", "published", "synced"].includes(key)) {
    return "bg-emerald-100 text-emerald-800";
  }
  if (["pending"].includes(key)) {
    return "bg-amber-100 text-amber-800";
  }
  if (["error"].includes(key)) {
    return "bg-red-100 text-red-800";
  }
  return "bg-gray-100 text-gray-700";
}

function normalizeWorkorderTypeKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

const COMPACT_PROJECT_WORKORDER_KEYS = new Set([
  "project",
  "offerte_project",
  "inspectie_controle",
  "opname_voorbereiding",
]);

const EXTENDED_EXECUTION_WORKORDER_KEYS = new Set([
  "service_storing",
  "regie",
  "wachttijd_geen_bon",
]);

function isProjectWorkorder(werkbon) {
  const candidates = [
    werkbon?.workorderType,
    werkbon?.type,
    werkbon?.workorderTypeConfig?.key,
    werkbon?.workorderTypeConfig?.systemKey,
  ]
    .map(normalizeWorkorderTypeKey)
    .filter(Boolean);
  if (candidates.some((key) => EXTENDED_EXECUTION_WORKORDER_KEYS.has(key))) return false;
  if (candidates.some((key) => COMPACT_PROJECT_WORKORDER_KEYS.has(key))) return true;
  return String(werkbon?.type || "").trim().toUpperCase() === "PROJECT";
}

function shouldHideHourTypePicker(werkbon, hourTypeOptions = []) {
  const optionCount = Array.isArray(hourTypeOptions) ? hourTypeOptions.length : 0;
  const defaultHourTypeId = String(
    werkbon?.completionState?.defaultHourTypeId ||
      werkbon?.completionFlow?.defaultHourTypeId ||
      werkbon?.workorderTypeConfig?.defaultHourTypeId ||
      ""
  ).trim();
  return Boolean(defaultHourTypeId) || optionCount <= 1;
}

function MarketingProofSummary({
  werkbon,
  completionFlow,
  visibleDaglogEntries,
  werkbonPhotos,
}) {
  const status = String(werkbon?.status || "-");
  const signatureRequired = completionFlow?.signatureRequired === true;
  const signatureReady = completionFlow?.signatureCaptured === true;
  const photoCount = Array.isArray(werkbonPhotos) ? werkbonPhotos.length : 0;
  const logbookCount = Array.isArray(visibleDaglogEntries) ? visibleDaglogEntries.length : 0;

  return (
    <div className="rounded border border-emerald-200 bg-emerald-50 p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800">
        Bewijsvoering
      </p>
      <div className="mt-2 grid gap-2 text-sm md:grid-cols-4">
        <SummaryPill
          label="Status"
          value={status}
          tone="bg-emerald-100 text-emerald-800"
        />
        <SummaryPill
          label="Klant-handtekening"
          value={signatureRequired ? (signatureReady ? "Vastgelegd" : "Nog nodig") : "Niet vereist"}
          tone={signatureReady ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}
        />
        <SummaryPill
          label="Resultaatfoto's"
          value={`${photoCount} stuk(s)`}
          tone={photoCount > 0 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}
        />
        <SummaryPill
          label="Digitaal logboek"
          value={logbookCount > 0 ? `${logbookCount} regel(s)` : "Nog leeg"}
          tone={logbookCount > 0 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}
        />
      </div>
    </div>
  );
}

function SummaryPill({ label, value, tone }) {
  return (
    <div className="rounded border border-white/70 bg-white p-2">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>
        {value}
      </span>
    </div>
  );
}

function CompletionFlowPanel({
  canAfrondenWerkbon,
  completionFlow,
  completionState,
  customerApprovalDraft,
  customerApprovalError,
  customerApprovalUsed,
  customerApprovalReady,
  updateCustomerApprovalField,
  clearCustomerApproval,
  showSignatureSection = false,
  signatureRequired = false,
  signatureEditable = true,
}) {
  const flow = completionFlow || {};
  const missingItems = getCompletionMissingItems(
    flow,
    completionState,
    customerApprovalReady,
    signatureRequired
  );
  const showSignature = showSignatureSection === true;
  const signatureCaptured = flow.signatureCaptured === true;
  const signatureReady = signatureCaptured || customerApprovalReady;
  const showWorkReport = flow.generateWorkReport === true;
  const showClientHub = flow.publishToClientHub === true;
  const showMoneybird = flow.moneybirdSync === true;
  const completionSummaryLabel =
    missingItems.length > 0
      ? `Nog ${missingItems.length} aandachtspunt(en)`
      : "Klaar om gereed te melden";
  const attentionRequired = missingItems.length > 0;

  return (
    <details
      className={`rounded border p-3 ${
        attentionRequired ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"
      }`}
      open={attentionRequired || Boolean(customerApprovalError)}
    >
      <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-2">
        <span className={`text-sm font-semibold ${attentionRequired ? "text-red-900" : "text-gray-900"}`}>
          Afronding
        </span>
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
            attentionRequired
              ? "bg-red-100 text-red-800"
              : "bg-emerald-100 text-emerald-800"
          }`}
        >
          {completionSummaryLabel}
        </span>
      </summary>

      <div className="mt-3">

        {missingItems.length > 0 ? (
          <div className="mb-3 rounded border border-red-200 bg-red-100 p-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-red-800">
              Nog nodig voor afronden
            </p>
            <ul className="mt-1 list-disc space-y-0.5 pl-4 text-sm text-red-900">
              {missingItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {showSignature && (
          <div className="mb-3 rounded border bg-white p-3">
            <p className="text-sm font-medium text-gray-900">Klant-handtekening</p>
            <p className="mt-1 text-xs text-gray-600">
              {signatureRequired
                ? "Dit werkbontype vereist een handtekening bij gereed melden."
                : "Klant-akkoord kan optioneel worden vastgelegd voor deze werkbon."}
            </p>
            {signatureReady ? (
              <p className="mt-2 text-sm text-emerald-700">
                {signatureCaptured
                  ? "Handtekening bevestigd."
                  : "Klant-akkoord is klaar om mee te sturen bij afronden."}
              </p>
            ) : null}
          </div>
        )}

        {showSignature && (
          <div className="rounded border bg-white p-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-gray-900">Klant-akkoord</p>
              <p className="mt-1 text-xs text-gray-600">
                Leg naam, akkoord en handtekening vast op het moment van afronden.
              </p>
            </div>
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                signatureRequired
                  ? "bg-amber-100 text-amber-800"
                  : "bg-slate-100 text-slate-700"
              }`}
            >
              {signatureRequired ? "Verplicht" : "Optioneel"}
            </span>
          </div>

          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <label className="text-sm font-medium text-gray-700">
              Naam ondertekenaar
              <input
                type="text"
                value={customerApprovalDraft?.signedByName || ""}
                onChange={(event) =>
                  updateCustomerApprovalField("signedByName", event.target.value)
                }
                disabled={!canAfrondenWerkbon || !signatureEditable}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                placeholder="Bijv. Jan Jansen"
              />
            </label>

            <label className="text-sm font-medium text-gray-700">
              Rol/functie
              <input
                type="text"
                value={customerApprovalDraft?.signedByRole || ""}
                onChange={(event) =>
                  updateCustomerApprovalField("signedByRole", event.target.value)
                }
                disabled={!canAfrondenWerkbon || !signatureEditable}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                placeholder="Optioneel"
              />
            </label>
          </div>

          <label className="mt-3 flex items-start gap-3 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={Boolean(customerApprovalDraft?.approved)}
              onChange={(event) =>
                updateCustomerApprovalField("approved", event.target.checked)
              }
              disabled={!canAfrondenWerkbon || !signatureEditable}
              className="mt-1"
            />
            <span>Akkoord met uitgevoerde werkzaamheden</span>
          </label>

          <div className="mt-3">
            <label
              htmlFor="customer-approval-signature"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Handtekening klant
            </label>
            <SignaturePadField
              id="customer-approval-signature"
              value={customerApprovalDraft?.signatureDataUrl || ""}
              onChange={(nextValue) =>
                updateCustomerApprovalField("signatureDataUrl", nextValue)
              }
              disabled={!canAfrondenWerkbon || !signatureEditable}
            />
          </div>
          {!signatureEditable ? (
            <p className="mt-3 text-xs text-slate-500">
              Klant-akkoord is zichtbaar, maar staat voor deze rol op alleen-lezen.
            </p>
          ) : null}

          {customerApprovalUsed ? (
            <div className="mt-3 flex items-center justify-between gap-3 rounded border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-xs text-slate-600">
                Dit klant-akkoord wordt meegestuurd bij afronden zodra alle velden geldig zijn.
              </p>
              <button
                type="button"
                onClick={clearCustomerApproval}
                disabled={!canAfrondenWerkbon || !signatureEditable}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Blok leegmaken
              </button>
            </div>
          ) : null}
          {customerApprovalError ? (
            <p className="mt-3 text-sm text-red-600">{customerApprovalError}</p>
          ) : null}
          </div>
        )}

        {(showWorkReport || showClientHub || showMoneybird) && (
          <div className="grid gap-2 md:grid-cols-3">
            {showWorkReport && (
              <div className="rounded border bg-white p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Werkrapport
                </p>
                <span
                  className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${getStatusPillClassName(
                    flow.workReportStatus
                  )}`}
                >
                  {flow.workReportStatus || "pending"}
                </span>
                <p className="mt-2 text-xs text-gray-600">
                  Wordt na afronden verder verwerkt.
                </p>
              </div>
            )}
            {showClientHub && (
              <div className="rounded border bg-white p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Klant Hub publicatie
                </p>
                <span
                  className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${getStatusPillClassName(
                    flow.clientHubStatus
                  )}`}
                >
                  {flow.clientHubStatus || "pending"}
                </span>
                <p className="mt-2 text-xs text-gray-600">
                  Publicatie volgt vanuit afrondingsflow.
                </p>
              </div>
            )}
            {showMoneybird && (
              <div className="rounded border bg-white p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Moneybird sync
                </p>
                <span
                  className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${getStatusPillClassName(
                    flow.moneybirdReadyForSync ? "ready" : "pending"
                  )}`}
                >
                  {flow.moneybirdReadyForSync ? "ready" : "pending"}
                </span>
                <p className="mt-2 text-xs text-gray-600">
                  {flow.moneybirdIntegrationEnabled
                    ? "Gereed voor sync na afronden."
                    : "Moneybird koppeling staat uit voor deze tenant."}
                </p>
              </div>
            )}
          </div>
        )}

        {!canAfrondenWerkbon && (
          <p className="mt-3 text-xs text-gray-500">
            Gereed melden wordt beschikbaar zodra de werkstatus in uitvoering staat.
          </p>
        )}
      </div>
    </details>
  );
}

function ExecutionTimeRequestsPanel({
  latestTimeExtensionRequest,
  setShowTimeExtensionModal,
  timeExtensionSuccess,
  timeExtensionError,
  showTimeExtensionModal,
  timeExtensionLoading,
  timeExtensionRequests,
}) {
  return (
    <details className="rounded border border-gray-200 bg-gray-50 p-3">
      <summary className="cursor-pointer font-semibold">Extra tijd</summary>
      <div className="mt-3 space-y-3">
        <ExtraTimeRequestButton
          latestRequest={latestTimeExtensionRequest}
          onClick={() => setShowTimeExtensionModal(true)}
          disabled={latestTimeExtensionRequest?.status === "pending"}
        />
        {timeExtensionSuccess ? <p className="text-sm text-green-700">{timeExtensionSuccess}</p> : null}
        {timeExtensionError && !showTimeExtensionModal ? (
          <p className="text-sm text-red-600">{timeExtensionError}</p>
        ) : null}

        <div className="rounded border border-gray-200 bg-white p-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">Extra tijd verzoeken</p>
              <p className="text-xs text-gray-500">Status van jouw aanvragen voor deze werkbon.</p>
            </div>
            {timeExtensionLoading ? <span className="text-xs text-gray-500">Laden...</span> : null}
          </div>

          {timeExtensionRequests.length === 0 ? (
            <p className="mt-3 text-sm text-gray-500">Nog geen extra tijd aangevraagd.</p>
          ) : (
            <div className="mt-3 space-y-2">
              {timeExtensionRequests.slice(0, 3).map((item) => (
                <div
                  key={item._id}
                  className="flex flex-wrap items-start justify-between gap-2 rounded border border-gray-200 p-3"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">+{item.requestedMinutes} min</p>
                    <p className="text-xs text-gray-500">{formatDatumTijd(item.createdAt)}</p>
                    {item.reason ? <p className="mt-1 text-sm text-gray-700">{item.reason}</p> : null}
                  </div>
                  <span
                    className={`rounded-full px-2 py-1 text-[11px] font-medium ${getTimeRequestStatusClassName(
                      item.status
                    )}`}
                  >
                    {formatTimeRequestStatus(item.status)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </details>
  );
}

export default function WerkbonExecutionTab(props) {
  const marketingDemoMode = useMarketingDemoMode();
  const cameraInputRef = React.useRef(null);
  const galleryInputRef = React.useRef(null);
  const {
    activeWerkbonId,
    werkbonLoading,
    werkbonError,
    werkbon,
    isAutoTimeMode,
    canStartWerkbon,
    canResumeWerkbon,
    canBlockWerkbon,
    canAfrondenWerkbon,
    canSelfAssignWerkbon,
    actieLoading,
    handleUnblockWerkbon,
    handleStartWerkbon,
    handleSelfAssignWerkbon,
    openStopReasonPicker,
    handleAfrondenWerkbon,
    showStopReasonPicker,
    STOP_REASON_OPTIONS,
    handleBlockWerkbon,
    lastStopReason,
    setShowStopReasonPicker,
    latestTimeExtensionRequest,
    setShowTimeExtensionModal,
    timeExtensionSuccess,
    timeExtensionError,
    showTimeExtensionModal,
    timeExtensionLoading,
    timeExtensionRequests,
    workorderDocumentsLoading,
    highlightedSharedDocuments,
    workorderDocuments,
    PHOTO_CATEGORY_OPTIONS,
    photoCategoryDraft,
    setPhotoCategoryDraft,
    handleSelectPhotos,
    photoError,
    photoSuccess,
    selectedPhotos,
    photoEditingId,
    handleEditSelectedPhoto,
    removeSelectedPhoto,
    handleUploadPhotos,
    photoUploading,
    photoUploadProgress,
    werkbonPhotos,
    handleDeleteWerkbonPhoto,
    photoDeletingId,
    hourDraft,
    setHourDraft,
    addHourDraft,
    hourTypeOptions,
    hourEntries,
    hourEntriesDirty,
    requireHoursForCompletion,
    autoAdjustments,
    adjustmentDraft,
    setAdjustmentDraft,
    ADJUSTMENT_CATEGORIES,
    addAdjustmentDraft,
    submitAutoTimeForApproval,
    timeSubmitLoading,
    canSubmitAutoTime,
    tuIntegrationStatus,
    startTuPunchout,
    tuPunchoutLoading,
    tuPunchoutError,
    tuPunchoutSuccess,
    downloadWorkorderPdf,
    workorderPdfLoading,
    materialSuggestions,
    materialSuggestionsLoading,
    materialSuggestionsError,
    materialSuggestionSearch,
    setMaterialSuggestionSearch,
    applyMaterialSuggestion,
    materialDraft,
    setMaterialDraft,
    addMaterialDraft,
    removeMaterialEntry,
    materialEntries,
    saveEntries,
    entriesSaving,
    handleDaglogSubmit,
    dailyLogForm,
    setDailyLogForm,
    setDailyLogSuccess,
    dailyLogSaving,
    dailyLogError,
    dailyLogSuccess,
    completionFlow,
    completionState,
    customerApprovalDraft,
    customerApprovalError,
    customerApprovalUsed,
    customerApprovalReady,
    updateCustomerApprovalField,
    clearCustomerApproval,
  } = props;
  const suggestionQuery = String(materialSuggestionSearch || "")
    .trim()
    .toLowerCase();
  const getSuggestionRelevance = (item) => {
    if (!suggestionQuery) return 0;
    const description = String(item?.description || "")
      .trim()
      .toLowerCase();
    const supplier = String(item?.supplier || "")
      .trim()
      .toLowerCase();
    const supplierArticleNumber = String(
      item?.supplierArticleNumber || item?.articleCode || ""
    )
      .trim()
      .toLowerCase();

    const fields = [description, supplier, supplierArticleNumber].filter(Boolean);
    if (fields.length === 0) return Number.POSITIVE_INFINITY;

    if (fields.some((value) => value === suggestionQuery)) return 0;
    if (fields.some((value) => value.startsWith(suggestionQuery))) return 1;
    if (description.includes(suggestionQuery)) return 2;
    if (supplier.includes(suggestionQuery)) return 3;
    if (supplierArticleNumber.includes(suggestionQuery)) return 4;
    return Number.POSITIVE_INFINITY;
  };
  const filterSuggestionItems = (items = []) => {
    if (!suggestionQuery) return items;
    return items
      .map((item, index) => ({
        item,
        index,
        relevance: getSuggestionRelevance(item),
      }))
      .filter((row) => Number.isFinite(row.relevance))
      .sort((a, b) => {
        if (a.relevance !== b.relevance) return a.relevance - b.relevance;
        return a.index - b.index;
      })
      .map((row) => row.item);
  };
  const filteredRecentSuggestions = filterSuggestionItems(
    materialSuggestions?.recent || []
  );
  const filteredProjectSuggestions = filterSuggestionItems(
    materialSuggestions?.projectOrdered || []
  );
  const visibleDaglogEntries = React.useMemo(() => {
    const structured = Array.isArray(werkbon?.dailyLogs)
      ? werkbon.dailyLogs
          .map((item, index) => ({
            id: String(item?._id || `daily-${index}`),
            date: item?.date || item?.updatedAt || item?.createdAt || null,
            text: String(item?.description || "").trim(),
            source: "daily_log",
          }))
          .filter((item) => item.text)
      : [];

    const legacy = Array.isArray(werkbon?.daglog)
      ? werkbon.daglog
          .map((item, index) => ({
            id: `legacy-${index}`,
            date: item?.datum || null,
            text: String(item?.omschrijving || "").trim(),
            source: "legacy_daglog",
          }))
          .filter((item) => item.text)
      : [];

    return [...structured, ...legacy]
      .sort((a, b) => {
        const aTime = new Date(a?.date || 0).getTime();
        const bTime = new Date(b?.date || 0).getTime();
        return bTime - aTime;
      })
      .filter((item, index, list) => {
        const currentKey = `${String(item.text || "")}|${new Date(item?.date || 0).getTime()}`;
        return index === list.findIndex((candidate) => {
          const candidateKey = `${String(candidate.text || "")}|${new Date(candidate?.date || 0).getTime()}`;
          return candidateKey === currentKey;
        });
      });
  }, [werkbon]);
  const demoIndex = Number(String(werkbon?._id || "").slice(-2), 16) || 0;
  const effectiveUiPolicy =
    werkbon?.effectiveUiPolicy && typeof werkbon.effectiveUiPolicy === "object"
      ? werkbon.effectiveUiPolicy
      : {};
  const effectiveLayout = String(effectiveUiPolicy.layout || "").trim().toLowerCase();
  const compactProjectLayout =
    effectiveLayout === "compact"
      ? true
      : effectiveLayout === "uitgebreid"
      ? false
      : isProjectWorkorder(werkbon);
  const showHoursSection =
    effectiveUiPolicy.showHours !== false && requireHoursForCompletion;
  const showMaterialsSection = effectiveUiPolicy.showMaterials !== false;
  const showSignatureSection = effectiveUiPolicy.showSignature !== false;
  const signatureRequired = effectiveUiPolicy.requireSignature === true;
  const showPhotosSection = effectiveUiPolicy.showPhotos !== false;
  const canEditHours = effectiveUiPolicy.editHours !== false;
  const canEditMaterials = effectiveUiPolicy.editMaterials !== false;
  const canEditPhotos = effectiveUiPolicy.editPhotos !== false;
  const canEditSignature = effectiveUiPolicy.editSignature !== false;
  const showPdfAction = effectiveUiPolicy.showPdf !== false;
  const canSaveEntries =
    (showMaterialsSection && canEditMaterials) ||
    (!isAutoTimeMode && showHoursSection && canEditHours) ||
    (isAutoTimeMode && effectiveUiPolicy.showHours !== false && canEditHours);
  const sanitizedWorkorderNumber = marketingDemoMode
    ? getDemoWorkorderNumber(demoIndex)
    : werkbon?.werkbonNummer || werkbon?.nummer || "-";
  const sanitizedLocation = marketingDemoMode
    ? getDemoLocationName()
    : getLocationSummary(werkbon, marketingDemoMode);
  const sanitizedSummaryTitle = marketingDemoMode
    ? getDemoWorkorderTitle(demoIndex)
    : werkbon?.omschrijving || werkbon?.opdracht?.omschrijving || "-";
  const clientSummary = marketingDemoMode ? getDemoClientName() : getClientSummary(werkbon);
  const contactSummary = getContactSummary(werkbon);
  const workScopeItems = getScopeItems(werkbon);
  const compactMaterialRows = getCompactMaterialRows(materialEntries);
  const hideHourTypePicker = shouldHideHourTypePicker(werkbon, hourTypeOptions);
  const latestWerkbonPhotos = React.useMemo(() => {
    if (!Array.isArray(werkbonPhotos) || werkbonPhotos.length === 0) return [];
    return [...werkbonPhotos]
      .sort((a, b) => {
        const aTime = new Date(a?.createdAt || 0).getTime();
        const bTime = new Date(b?.createdAt || 0).getTime();
        return bTime - aTime;
      })
      .slice(0, 3);
  }, [werkbonPhotos]);
  const executionMainActionsProps = {
    werkbon,
    canStartWerkbon,
    canResumeWerkbon,
    canBlockWerkbon,
    canAfrondenWerkbon,
    canSelfAssignWerkbon,
    actieLoading,
    handleUnblockWerkbon,
    handleStartWerkbon,
    handleSelfAssignWerkbon,
    openStopReasonPicker,
    handleAfrondenWerkbon,
    showStopReasonPicker,
    STOP_REASON_OPTIONS,
    handleBlockWerkbon,
    lastStopReason,
    setShowStopReasonPicker,
    werkbonError,
    showPdfAction,
    downloadWorkorderPdf,
    workorderPdfLoading,
  };

  return (
    <section>
      <div className="mb-3">
        <h3 className="text-lg font-semibold">Werk uitvoeren</h3>
        <p className="text-sm text-gray-500">Focus op status, hoofdacties en registratie.</p>
      </div>
      {!activeWerkbonId && (
        <p className="text-gray-500">Selecteer eerst een werkbon via de agenda of bonnenlijst.</p>
      )}
      {activeWerkbonId && werkbonLoading && <p className="text-gray-500">Werkbon laden...</p>}
      {activeWerkbonId && werkbonError && <p className="text-red-600">{werkbonError}</p>}
      {werkbon && !werkbonLoading && (
        <div className="space-y-4">
          {marketingDemoMode ? (
            <MarketingProofSummary
              werkbon={werkbon}
              completionFlow={completionFlow}
              visibleDaglogEntries={visibleDaglogEntries}
              werkbonPhotos={werkbonPhotos}
            />
          ) : null}

          <ExecutionOverviewPanel
            werkbon={werkbon}
            compactProjectLayout={compactProjectLayout}
            sanitizedWorkorderNumber={sanitizedWorkorderNumber}
            sanitizedSummaryTitle={sanitizedSummaryTitle}
            clientSummary={clientSummary}
            sanitizedLocation={sanitizedLocation}
            contactSummary={contactSummary}
            isAutoTimeMode={isAutoTimeMode}
            latestWerkbonPhotos={latestWerkbonPhotos}
            compactMaterialRows={compactMaterialRows}
            workScopeItems={workScopeItems}
            actionsProps={executionMainActionsProps}
          />

          <details className="rounded border border-slate-200 bg-slate-50 p-3" open>
            <summary className="cursor-pointer font-semibold">Werkopdracht</summary>
            <div className="mt-3 space-y-3">
              <div className="rounded border border-slate-200 bg-white p-3">
                <p className="whitespace-pre-line text-sm leading-relaxed text-slate-700">
                  {sanitizedSummaryTitle}
                </p>
              </div>
              {workScopeItems.length > 0 ? (
                <ul className="space-y-2 text-sm text-slate-700">
                  {workScopeItems.map((item, index) => (
                    <li key={`scope-${index}`} className="rounded border border-slate-200 bg-white px-3 py-2">
                      {item}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </details>

          <CompletionFlowPanel
            canAfrondenWerkbon={canAfrondenWerkbon}
            completionFlow={completionFlow}
            completionState={completionState}
            customerApprovalDraft={customerApprovalDraft}
            customerApprovalError={customerApprovalError}
            customerApprovalUsed={customerApprovalUsed}
            customerApprovalReady={customerApprovalReady}
            updateCustomerApprovalField={updateCustomerApprovalField}
            clearCustomerApproval={clearCustomerApproval}
            showSignatureSection={showSignatureSection}
            signatureRequired={signatureRequired}
            signatureEditable={canEditSignature}
          />

          {!compactProjectLayout && (
            <ExecutionTimeRequestsPanel
              latestTimeExtensionRequest={latestTimeExtensionRequest}
              setShowTimeExtensionModal={setShowTimeExtensionModal}
              timeExtensionSuccess={timeExtensionSuccess}
              timeExtensionError={timeExtensionError}
              showTimeExtensionModal={showTimeExtensionModal}
              timeExtensionLoading={timeExtensionLoading}
              timeExtensionRequests={timeExtensionRequests}
            />
          )}

          {!isAutoTimeMode && showHoursSection && (
            <details className="rounded border p-3 space-y-3">
              <summary className="cursor-pointer font-semibold">
                {compactProjectLayout ? "Urenregistratie" : "Tijdregistratie (handmatig)"}
              </summary>
              <div className="mt-2">
                <span
                  className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                    hourEntriesDirty
                      ? "bg-amber-100 text-amber-800"
                      : "bg-emerald-100 text-emerald-800"
                  }`}
                >
                  {hourEntriesDirty ? "Niet opgeslagen" : "Opgeslagen"}
                </span>
                <p className="mt-1 text-xs text-gray-500">
                  Urenregistratie is verplicht voor dit werkbontype.
                </p>
                {!canEditHours ? (
                  <p className="mt-1 text-xs text-slate-500">
                    Uren zijn zichtbaar, maar voor deze rol niet wijzigbaar.
                  </p>
                ) : null}
              </div>
              {canEditHours ? (
                <div
                  className={`mt-3 grid gap-2 ${
                    hideHourTypePicker
                      ? "md:grid-cols-[2fr_1fr_auto]"
                      : "md:grid-cols-[1.5fr_2fr_1fr_auto]"
                  }`}
                >
                  {!hideHourTypePicker && (
                    <select
                      value={hourDraft.hourTypeId || ""}
                      onChange={(e) =>
                        setHourDraft((prev) => ({ ...prev, hourTypeId: e.target.value }))
                      }
                      className="rounded border p-2 text-sm"
                    >
                      {hourTypeOptions.length === 0 && (
                        <option value="">Geen uursoorten gevonden</option>
                      )}
                      {hourTypeOptions.map((item) => (
                        <option key={item._id} value={item._id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  )}
                  <input
                    type="text"
                    value={hourDraft.description}
                    onChange={(e) =>
                      setHourDraft((prev) => ({ ...prev, description: e.target.value }))
                    }
                    placeholder="Omschrijving uren"
                    className="rounded border p-2 text-sm"
                  />
                  <input
                    type="number"
                    step="0.25"
                    min="0"
                    value={hourDraft.hours}
                    onChange={(e) => setHourDraft((prev) => ({ ...prev, hours: e.target.value }))}
                    placeholder="Uren"
                    className="rounded border p-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={addHourDraft}
                    className="rounded bg-gray-800 px-3 py-2 text-sm text-white hover:bg-gray-900"
                  >
                    Uur toevoegen
                  </button>
                </div>
              ) : null}
              <ul className="space-y-2">
                {hourEntries.map((entry, idx) => (
                  <li key={`hour-${idx}`} className="rounded bg-gray-50 p-2 text-sm">
                    <p>
                      {entry.hourTypeName ? `${entry.hourTypeName} • ` : ""}
                      {entry.description || "Uurregel"} • {Number(entry.hours || 0).toFixed(2)} uur
                    </p>
                  </li>
                ))}
                {hourEntries.length === 0 && (
                  <li className="rounded bg-gray-50 p-2 text-sm text-gray-500">
                    Nog geen uurregels toegevoegd.
                  </li>
                )}
              </ul>
            </details>
          )}

          {isAutoTimeMode && !compactProjectLayout && effectiveUiPolicy.showHours !== false && (
            <details className="rounded border p-3 space-y-3">
              <summary className="cursor-pointer font-semibold">Tijdcorrecties (automatisch)</summary>
              {!canEditHours ? (
                <p className="mt-3 text-xs text-slate-500">
                  Tijdcorrecties zijn zichtbaar, maar voor deze rol niet wijzigbaar.
                </p>
              ) : (
                <div className="mt-3 grid gap-2 md:grid-cols-[1fr_2fr_1fr_2fr_auto]">
                  <select
                    value={adjustmentDraft.category}
                    onChange={(e) =>
                      setAdjustmentDraft((prev) => ({ ...prev, category: e.target.value }))
                    }
                    className="rounded border p-2 text-sm"
                  >
                    {ADJUSTMENT_CATEGORIES.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={adjustmentDraft.description}
                    onChange={(e) =>
                      setAdjustmentDraft((prev) => ({ ...prev, description: e.target.value }))
                    }
                    placeholder="Omschrijving"
                    className="rounded border p-2 text-sm"
                  />
                  <input
                    type="number"
                    step="0.25"
                    min="0"
                    value={adjustmentDraft.hours}
                    onChange={(e) =>
                      setAdjustmentDraft((prev) => ({ ...prev, hours: e.target.value }))
                    }
                    placeholder="Uren"
                    className="rounded border p-2 text-sm"
                  />
                  <input
                    type="text"
                    value={adjustmentDraft.reason}
                    onChange={(e) =>
                      setAdjustmentDraft((prev) => ({ ...prev, reason: e.target.value }))
                    }
                    placeholder="Reden correctie"
                    className="rounded border p-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={addAdjustmentDraft}
                    className="rounded bg-gray-800 px-3 py-2 text-sm text-white hover:bg-gray-900"
                  >
                    Correctie toevoegen
                  </button>
                </div>
              )}
              <ul className="space-y-2">
                {autoAdjustments.map((entry, idx) => (
                  <li key={`adj-${idx}`} className="rounded bg-gray-50 p-2 text-sm">
                    <p>
                      {entry.description || entry.reason || "Correctie"} •{" "}
                      {Number(entry.hours || 0).toFixed(2)} uur
                    </p>
                    {entry.reason ? <p className="text-xs text-gray-600">{entry.reason}</p> : null}
                  </li>
                ))}
                {autoAdjustments.length === 0 && (
                  <li className="rounded bg-gray-50 p-2 text-sm text-gray-500">
                    Nog geen correcties toegevoegd.
                  </li>
                )}
              </ul>
              {canEditHours ? (
                <button
                  type="button"
                  onClick={submitAutoTimeForApproval}
                  disabled={!canSubmitAutoTime || timeSubmitLoading}
                  className="rounded border border-blue-300 bg-white px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-60"
                >
                  {timeSubmitLoading ? "Indienen..." : "Uren indienen ter goedkeuring"}
                </button>
              ) : null}
            </details>
          )}

          {String(werkbon?.status || "").toLowerCase() === "tijdelijk_stilgelegd" && (
            <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
              <p className="font-semibold">Werk tijdelijk stilgelegd</p>
              <p>{werkbon?.blockedReasonText || "Geen blokkadereden ingevuld."}</p>
            </div>
          )}

          {showPhotosSection && (
            <details className="rounded border p-3 space-y-3">
              <summary className="cursor-pointer font-semibold">
                {compactProjectLayout ? "Resultaatfoto's en bewijs" : "Resultaatfoto's"}
              </summary>
              <div className="mt-3 space-y-3">
                {canEditPhotos ? (
                  <>
                    <div className="flex flex-wrap gap-2">
                      {PHOTO_CATEGORY_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setPhotoCategoryDraft(option.value)}
                          className={`min-h-[44px] rounded-full border px-3 py-2 text-xs font-medium ${
                            photoCategoryDraft === option.value
                              ? "border-blue-600 bg-blue-50 text-blue-700"
                              : "border-gray-300 bg-white text-gray-700"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>

                    <label className="block text-sm font-medium text-gray-700">Foto's kiezen</label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => cameraInputRef.current?.click()}
                        disabled={photoUploading || Boolean(photoEditingId)}
                        className="min-h-[44px] rounded-lg border border-blue-300 bg-white px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-60"
                      >
                        Foto maken
                      </button>
                      <button
                        type="button"
                        onClick={() => galleryInputRef.current?.click()}
                        disabled={photoUploading || Boolean(photoEditingId)}
                        className="min-h-[44px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-60"
                      >
                        Uit galerij
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-slate-500">
                    Resultaatfoto's zijn zichtbaar, maar voor deze rol niet wijzigbaar.
                  </p>
                )}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                multiple
                capture="environment"
                onChange={handleSelectPhotos}
                className="hidden"
              />
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleSelectPhotos}
                className="hidden"
              />
              <p className="text-xs text-gray-500">
                Kies "Foto maken" voor camera of "Uit galerij" voor bestaande foto's.
              </p>
              {photoError && <p className="text-sm text-red-600">{photoError}</p>}
              {photoSuccess && <p className="text-sm text-green-700">{photoSuccess}</p>}

              {canEditPhotos && selectedPhotos.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">
                    Preview vóór upload ({selectedPhotos.length})
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedPhotos.map((photo) => (
                      <div key={photo.id} className="relative">
                        <img
                          src={photo.previewUrl}
                          alt="Preview"
                          className="aspect-square w-full rounded-lg object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => handleEditSelectedPhoto(photo.id)}
                          disabled={photoEditingId === photo.id}
                          className="absolute left-1 top-1 rounded bg-blue-600/80 px-2 py-1 text-[10px] text-white disabled:opacity-60"
                        >
                          {photoEditingId === photo.id ? "..." : "Bewerk"}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeSelectedPhoto(photo.id)}
                          className="absolute right-1 top-1 rounded bg-black/70 px-2 py-1 text-[10px] text-white"
                        >
                          X
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={handleUploadPhotos}
                    disabled={photoUploading || Boolean(photoEditingId)}
                    className="min-h-[48px] rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    {photoUploading ? "Uploaden..." : "Foto's uploaden"}
                  </button>

                  {photoUploading && (
                    <div className="h-2 w-full overflow-hidden rounded bg-gray-200">
                      <div
                        className="h-full bg-blue-600 transition-all"
                        style={{ width: `${photoUploadProgress}%` }}
                      />
                    </div>
                  )}
                </div>
              )}

              <div>
                <p className="mb-2 text-sm font-medium text-gray-700">Bestaande foto's</p>
                {werkbonPhotos.length === 0 ? (
                  <p className="text-sm text-gray-500">Nog geen foto's geüpload.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {werkbonPhotos
                      .slice()
                      .reverse()
                      .map((photo) => (
                        <div key={photo.id} className="group">
                          <a href={photo.url} target="_blank" rel="noreferrer" className="block">
                            <img
                              src={photo.thumbUrl || photo.url}
                              alt={`Foto ${photo.category}`}
                              onError={(event) => {
                                const target = event.currentTarget;
                                if (photo.url && target.src !== photo.url) {
                                  target.src = photo.url;
                                }
                              }}
                              className="aspect-square w-full rounded-lg object-cover"
                            />
                          </a>
                          <div className="mt-1 flex items-center justify-between text-[11px] text-gray-600">
                            <span className="rounded bg-gray-100 px-1 py-0.5">
                              {formatPhotoCategory(photo.category)}
                            </span>
                            <span>{formatShortDate(photo.createdAt)}</span>
                          </div>
                          {canEditPhotos && !photo.id.startsWith("legacy-") && (
                            <button
                              type="button"
                              onClick={() => handleDeleteWerkbonPhoto(photo.id)}
                              disabled={photoDeletingId === photo.id}
                              className="mt-1 w-full rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
                            >
                              {photoDeletingId === photo.id ? "Verwijderen..." : "Verwijder"}
                            </button>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
              </div>
            </details>
          )}

          <details className="rounded border p-3 space-y-3">
            <summary className="cursor-pointer font-semibold">
              {compactProjectLayout ? "Documenten en tekeningen" : "Relevante documenten"}
            </summary>
            <div className="mt-3 space-y-3">
              {workorderDocumentsLoading ? (
                <p className="text-sm text-gray-500">Documenten laden...</p>
              ) : (
                <>
                  <div>
                    <p className="mb-2 text-sm font-medium text-gray-700">
                      Belangrijke handleidingen en tekeningen
                    </p>
                    {highlightedSharedDocuments.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        Geen belangrijke gedeelde documenten beschikbaar.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {highlightedSharedDocuments.map((doc) => {
                          const href = toDocumentUrl(doc?.url || doc?.bestand || "");
                          const previewable = shouldPreviewDocument(doc);
                          const downloadName = doc?.name || doc?.title || "document";
                          return (
                          <div
                            key={doc._id}
                            className="flex flex-wrap items-center justify-between gap-2 rounded border border-amber-200 bg-amber-50 p-3"
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {doc.title || doc.name || "Document"}
                              </p>
                              <p className="text-xs text-gray-600">
                                {doc.category || "document"} |{" "}
                                {marketingDemoMode
                                  ? "Uploader verborgen (demo)"
                                  : doc.uploadedByName || "onbekend"}{" "}
                                |{" "}
                                {formatDatumTijd(doc.uploadedAt)}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <a
                                href={href}
                                target={previewable ? "_blank" : undefined}
                                rel={previewable ? "noreferrer" : undefined}
                                download={previewable ? undefined : downloadName}
                                className="rounded border px-2 py-1 text-xs text-gray-700 hover:bg-white"
                              >
                                {previewable ? "Open" : "Download"}
                              </a>
                              {previewable ? (
                                <a
                                  href={href}
                                  download={downloadName}
                                  className="rounded border px-2 py-1 text-xs text-gray-700 hover:bg-white"
                                >
                                  Download
                                </a>
                              ) : null}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="mb-2 text-sm font-medium text-gray-700">Projectdocumenten</p>
                    {workorderDocuments.projectDocuments?.length ? (
                      <div className="space-y-2">
                        {workorderDocuments.projectDocuments.slice(0, 5).map((doc) => (
                          <DocumentRow key={doc._id} doc={doc} />
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Geen projectdocumenten gekoppeld.</p>
                    )}
                  </div>

                  <div>
                    <p className="mb-2 text-sm font-medium text-gray-700">Werkbonbijlagen</p>
                    {workorderDocuments.workorderDocuments?.length ? (
                      <div className="space-y-2">
                        {workorderDocuments.workorderDocuments.map((doc) => (
                          <DocumentRow key={doc._id} doc={doc} />
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Nog geen werkbonbijlagen toegevoegd.</p>
                    )}
                  </div>
                </>
              )}
            </div>
          </details>

          {showMaterialsSection && (
            <details className="rounded border p-3 space-y-3">
              <summary className="cursor-pointer font-semibold">
                {compactProjectLayout ? "Materiaalregistratie" : "Materiaalregistratie"}
              </summary>
              <div className="mt-3 space-y-3">
              {!compactProjectLayout && canEditMaterials && (
                <>
                  <div className="rounded border border-emerald-200 bg-emerald-50 p-2 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p>
                        TU status: <strong>{tuIntegrationStatus.status || "none"}</strong>
                      </p>
                      <button
                        onClick={startTuPunchout}
                        disabled={tuPunchoutLoading}
                        className="rounded bg-emerald-600 px-3 py-2 text-xs text-white hover:bg-emerald-700 disabled:opacity-60"
                      >
                        {tuPunchoutLoading ? "Catalogus openen..." : "Open OCI catalogus"}
                      </button>
                    </div>
                    {tuPunchoutError && <p className="mt-1 text-xs text-red-700">{tuPunchoutError}</p>}
                    {tuPunchoutSuccess && <p className="mt-1 text-xs text-emerald-700">{tuPunchoutSuccess}</p>}
                  </div>
                  <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm">
                    <p className="mb-2 font-medium text-slate-800">Slimme suggesties</p>
                    <input
                      type="text"
                      value={materialSuggestionSearch || ""}
                      onChange={(e) => setMaterialSuggestionSearch(e.target.value)}
                      placeholder="Zoek op naam, leverancier of art.nr"
                      className="mb-2 w-full rounded border border-slate-300 bg-white p-2 text-sm"
                    />
                    {materialSuggestionsLoading && (
                      <p className="text-xs text-slate-600">Suggesties laden...</p>
                    )}
                    {materialSuggestionsError && (
                      <p className="text-xs text-red-700">{materialSuggestionsError}</p>
                    )}
                    {!materialSuggestionsLoading && !materialSuggestionsError && (
                      <div className="space-y-3">
                        <div>
                          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Recent
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {filteredRecentSuggestions.slice(0, 8).map((item) => (
                              <button
                                key={`recent-${item.key}`}
                                type="button"
                                onClick={() => applyMaterialSuggestion(item)}
                                className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-xs text-slate-700 hover:border-slate-400"
                              >
                                {item.description}
                              </button>
                            ))}
                            {filteredRecentSuggestions.length === 0 && (
                              <p className="text-xs text-slate-500">Nog geen recente suggesties.</p>
                            )}
                          </div>
                        </div>
                        <div>
                          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Op dit project besteld
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {filteredProjectSuggestions.slice(0, 8).map((item) => (
                              <button
                                key={`project-${item.key}`}
                                type="button"
                                onClick={() => applyMaterialSuggestion(item)}
                                className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs text-blue-700 hover:border-blue-300"
                              >
                                {item.description}
                              </button>
                            ))}
                            {filteredProjectSuggestions.length === 0 && (
                              <p className="text-xs text-slate-500">
                                Nog geen projectbestellingen gevonden.
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
              {!canEditMaterials ? (
                <p className="text-xs text-slate-500">
                  Materiaalregistratie is zichtbaar, maar voor deze rol niet wijzigbaar.
                </p>
              ) : (
                <div className={`grid gap-2 ${compactProjectLayout ? "md:grid-cols-[2fr_1fr_1fr_auto]" : "md:grid-cols-7"}`}>
                  <input
                    type="text"
                    value={materialDraft.description}
                    onChange={(e) =>
                      setMaterialDraft((prev) => ({ ...prev, description: e.target.value }))
                    }
                    placeholder="Materiaal"
                    className="rounded border p-2 text-sm"
                  />
                  <input
                    type="number"
                    step="1"
                    value={materialDraft.qty}
                    onChange={(e) => setMaterialDraft((prev) => ({ ...prev, qty: e.target.value }))}
                    placeholder="Aantal"
                    className="rounded border p-2 text-sm"
                  />
                  <input
                    type="text"
                    value={materialDraft.unit}
                    onChange={(e) => setMaterialDraft((prev) => ({ ...prev, unit: e.target.value }))}
                    placeholder="Eenheid"
                    className="rounded border p-2 text-sm"
                  />
                  {!compactProjectLayout && (
                    <>
                      <input
                        type="text"
                        value={materialDraft.supplier || ""}
                        onChange={(e) =>
                          setMaterialDraft((prev) => ({ ...prev, supplier: e.target.value }))
                        }
                        placeholder="Leverancier"
                        className="rounded border p-2 text-sm"
                      />
                      <input
                        type="text"
                        value={materialDraft.supplierArticleNumber || ""}
                        onChange={(e) =>
                          setMaterialDraft((prev) => ({
                            ...prev,
                            supplierArticleNumber: e.target.value,
                          }))
                        }
                        placeholder="Leverancier art.nr"
                        className="rounded border p-2 text-sm"
                      />
                      <input
                        type="number"
                        step="0.01"
                        value={materialDraft.unitSell}
                        onChange={(e) =>
                          setMaterialDraft((prev) => ({ ...prev, unitSell: e.target.value }))
                        }
                        placeholder="Verkoop/st"
                        className="rounded border p-2 text-sm"
                      />
                    </>
                  )}
                  <button
                    onClick={addMaterialDraft}
                    className="rounded bg-gray-800 px-3 py-2 text-sm text-white hover:bg-gray-900"
                  >
                    Materiaal toevoegen
                  </button>
                </div>
              )}
              <ul className="space-y-2">
                {materialEntries.map((entry, idx) => (
                  <li key={`mat-${idx}`} className="rounded bg-gray-50 p-2 text-sm">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p>
                          {entry.description} • {entry.qty} {entry.unit || "st"}
                        </p>
                        {(entry.supplier || entry.supplierArticleNumber || entry.articleCode) && (
                          <p className="text-xs text-gray-600">
                            {[entry.supplier, entry.supplierArticleNumber || entry.articleCode]
                              .filter(Boolean)
                              .join(" • ")}
                          </p>
                        )}
                      </div>
                      {canEditMaterials ? (
                        <button
                          type="button"
                          onClick={() => removeMaterialEntry(idx)}
                          className="rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                        >
                          Verwijder
                        </button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
              </div>
            </details>
          )}

          <div className="rounded border p-3">
            {canSaveEntries ? (
              <button
                onClick={saveEntries}
                disabled={entriesSaving}
                className="rounded bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700 disabled:opacity-60"
              >
                {entriesSaving
                  ? "Opslaan..."
                  : isAutoTimeMode
                  ? "Materiaal + correcties opslaan"
                  : requireHoursForCompletion
                  ? "Uren + materiaal opslaan"
                  : "Materiaal opslaan"}
              </button>
            ) : (
              <p className="text-sm text-slate-500">
                Uren en materialen staan voor deze rol op alleen-lezen.
              </p>
            )}
          </div>

          <details className="rounded border p-3">
            <summary className="cursor-pointer font-semibold">Daglog</summary>
            <div className="mt-3">
              <div className="mb-3 rounded border border-gray-300 p-3">
                <h5 className="mb-2 text-sm font-semibold">Daglog vandaag</h5>
                <form onSubmit={handleDaglogSubmit} className="space-y-3">
                  <textarea
                    value={dailyLogForm.description}
                    onChange={(e) => {
                      setDailyLogForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }));
                      setDailyLogSuccess("");
                    }}
                    rows={3}
                    placeholder="Wat is vandaag uitgevoerd, getest of besproken?"
                    className="w-full rounded border border-gray-300 p-2 text-sm"
                  />

                  <button
                    type="submit"
                    disabled={dailyLogSaving}
                    className="min-h-[44px] w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    {dailyLogSaving ? "Opslaan..." : "Daglog opslaan"}
                  </button>
                  {dailyLogError && <p className="text-sm text-red-600">{dailyLogError}</p>}
                  {dailyLogSuccess && <p className="text-sm text-green-700">{dailyLogSuccess}</p>}
                </form>
              </div>

              {visibleDaglogEntries.length === 0 ? (
                <p className="text-sm text-gray-500">Nog geen daglogregels.</p>
              ) : (
                <ul className="space-y-2">
                  {visibleDaglogEntries.map((item, idx) => (
                      <li key={`${item.id}-${idx}`} className="rounded border p-3">
                        <p className="text-xs text-gray-500">
                          {formatDatumTijd(item.date)}{" "}
                          {item.source === "daily_log" ? "• Daglog" : "• Legacy"}
                        </p>
                        <p className="text-sm">{item.text}</p>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          </details>
        </div>
      )}
    </section>
  );
}
